import { useEffect, useMemo, useRef, useState } from "react";
import { Link, NavLink, useLocation, useNavigate } from "react-router-dom";
import { AnimatePresence, motion, type Variants } from "motion/react";
import { ArrowRight, ChevronDown, Menu, X } from "lucide-react";
import { PREINSCRIPTION_URL } from "../data/site";
import { easeSoft } from "../lib/motion";

// Panneau desktop : toujours monté, on anime ouvert/fermé (pas d'AnimatePresence
// en hover, qui provoquait l'ouverture "une fois sur deux").
const desktopPanelVariants: Variants = {
  open: {
    opacity: 1,
    y: 0,
    scale: 1,
    pointerEvents: "auto",
    transition: { duration: 0.3, ease: easeSoft, staggerChildren: 0.07, delayChildren: 0.04 },
  },
  closed: {
    opacity: 0,
    y: -8,
    scale: 0.98,
    pointerEvents: "none",
    transition: { duration: 0.18, ease: easeSoft },
  },
};

const desktopItemVariants: Variants = {
  open: { opacity: 1, y: 0, transition: { duration: 0.5, ease: easeSoft } },
  closed: { opacity: 0, y: 10, transition: { duration: 0.12 } },
};

const navLinks = [
  { to: "/nos-activites", label: "Nos activités" },
  { to: "/tarifs", label: "Tarifs" },
  { to: "/entreprises", label: "Entreprises" },
  { to: "/nous-rejoindre", label: "Recrutement" },
];

type CrecheItem = { slug: string; nom: string };

const crechesMenu: CrecheItem[] = [
  { slug: "maestro-notre-dame-de-bondeville", nom: "Notre-Dame-de-Bondeville 1 - Maestro" },
  { slug: "arpege-notre-dame-de-bondeville", nom: "Notre-Dame-de-Bondeville 2 - Arpège" },
  { slug: "stm1-saint-marcel", nom: "Saint-Marcel 1 - Carillons" },
  { slug: "stm2-saint-marcel", nom: "Saint-Marcel 2 - Pianotins" },
  { slug: "cde-vernon", nom: "Vernon (Campus de l’Espace) - Hautbois" },
  { slug: "rouen-beauvoisine", nom: "Rouen Beauvoisine - Tempo" },
  { slug: "rouen-malouet", nom: "Rouen Malouet - Sonatine" },
  { slug: "rouen-saint-vivien", nom: "Rouen Saint-Vivien - Mélodie" },
  { slug: "isneauville", nom: "Isneauville - Symphonie" },
  { slug: "msa1-mont-saint-aignan", nom: "Mont-Saint-Aignan 1 - Muses" },
  { slug: "msa2-mont-saint-aignan", nom: "Mont-Saint-Aignan 2 - Les P’tits Accords" },
  { slug: "armonia-saint-etienne-du-rouvray", nom: "Saint-Etienne-du-Rouvray - Harmonia" },
];

function buildCreches(): CrecheItem[] {
  return crechesMenu;
}

export default function Header() {
  const [open, setOpen] = useState(false);
  const [desktopOpen, setDesktopOpen] = useState(false);
  const [mobileCrechesOpen, setMobileCrechesOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const closeTimer = useRef<number | null>(null);
  const navigate = useNavigate();
  const { pathname } = useLocation();

  const crechesList = useMemo(buildCreches, []);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 12);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    setOpen(false);
    setMobileCrechesOpen(false);
  }, [pathname]);

  useEffect(() => {
    if (!open) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    const onKey = (e: KeyboardEvent) => e.key === "Escape" && setOpen(false);
    window.addEventListener("keydown", onKey);
    return () => {
      document.body.style.overflow = prev;
      window.removeEventListener("keydown", onKey);
    };
  }, [open]);

  function openDesktop() {
    if (closeTimer.current) {
      window.clearTimeout(closeTimer.current);
      closeTimer.current = null;
    }
    setDesktopOpen(true);
  }

  function scheduleCloseDesktop() {
    if (closeTimer.current) window.clearTimeout(closeTimer.current);
    closeTimer.current = window.setTimeout(() => {
      setDesktopOpen(false);
    }, 350);
  }

  function closeAll() {
    setOpen(false);
    setMobileCrechesOpen(false);
  }

  return (
    <header
      className={`sticky top-0 z-50 border-b border-edge-soft transition-all duration-300 ${
        scrolled
          ? "bg-cream/95 backdrop-blur-md shadow-soft-sm"
          : "bg-cream/85 backdrop-blur"
      }`}
    >
      <div
        className={`max-w-7xl mx-auto px-4 md:px-8 flex items-center justify-between transition-[padding] duration-300 ${
          scrolled ? "py-2.5" : "py-4"
        }`}
      >
        <Link to="/" className="flex items-center" aria-label="La La Land — Accueil">
          <img
            src="/images/logo-lalaland.png"
            alt="La La Land micro-crèches"
            className={`w-auto transition-[height] duration-300 ${
              scrolled ? "h-10 md:h-11" : "h-12 md:h-14"
            }`}
            width={800}
            height={518}
          />
        </Link>

        <nav className="hidden md:flex items-center gap-7">
          <div
            className="relative"
            onMouseEnter={openDesktop}
            onMouseLeave={scheduleCloseDesktop}
          >
            <button
              type="button"
              onClick={() => navigate("/nos-micro-creches")}
              className="flex items-center gap-1 text-sm font-medium text-ink hover:text-terra transition-colors"
              aria-haspopup="true"
              aria-expanded={desktopOpen}
            >
              Nos micro-crèches
              <ChevronDown
                className={`w-4 h-4 transition-transform ${desktopOpen ? "rotate-180" : ""}`}
                strokeWidth={2}
              />
            </button>

            <motion.div
              className="absolute right-0 top-full pt-3"
              style={{ transformOrigin: "top right" }}
              variants={desktopPanelVariants}
              initial={false}
              animate={desktopOpen ? "open" : "closed"}
              onMouseEnter={openDesktop}
              onMouseLeave={scheduleCloseDesktop}
            >
              <div className="bg-sage rounded-2xl shadow-soft-lg overflow-hidden min-w-[320px]">
                <ul className="py-3 px-2">
                  {crechesList.map((c) => (
                    <motion.li key={c.slug} variants={desktopItemVariants}>
                      <Link
                        to={`/micro-creches/${c.slug}`}
                        onClick={() => setDesktopOpen(false)}
                        className="block px-4 py-2.5 rounded-xl text-sm text-white/95 hover:bg-white/15 transition-colors"
                      >
                        {c.nom}
                      </Link>
                    </motion.li>
                  ))}
                </ul>
              </div>
            </motion.div>
          </div>

          {navLinks.map((l) => (
            <NavLink
              key={l.to}
              to={l.to}
              className={({ isActive }) =>
                `text-sm font-medium transition-colors ${
                  isActive ? "text-terra" : "text-ink hover:text-terra"
                }`
              }
            >
              {l.label}
            </NavLink>
          ))}
          <a
            href={PREINSCRIPTION_URL}
            target="_blank"
            rel="noopener noreferrer"
            className="bg-terra hover:bg-terra-hover text-white text-sm font-medium px-5 py-2.5 rounded-xl transition active:scale-[0.97]"
          >
            Pré-inscription
          </a>
        </nav>

        <button
          aria-label={open ? "Fermer le menu" : "Menu"}
          aria-expanded={open}
          className="md:hidden p-2 -mr-2 rounded-xl hover:bg-muted"
          onClick={() => setOpen((v) => !v)}
        >
          {open ? <X strokeWidth={1.5} /> : <Menu strokeWidth={1.5} />}
        </button>
      </div>

      <AnimatePresence>
        {open && (
          <motion.div
            className="md:hidden absolute inset-x-0 top-full h-[100dvh] border-t border-edge-soft bg-cream overflow-y-auto overscroll-contain"
            initial={{ opacity: 0, y: -8 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -8 }}
            transition={{ duration: 0.25, ease: easeSoft }}
          >
            <div className="max-w-7xl mx-auto px-4 pt-4 pb-40 flex flex-col gap-1">
            <button
              type="button"
              onClick={() => setMobileCrechesOpen((v) => !v)}
              className="flex items-center justify-between px-3 py-3 rounded-xl text-base font-medium text-ink hover:bg-muted"
              aria-expanded={mobileCrechesOpen}
            >
              <span>Nos micro-crèches</span>
              <ChevronDown
                className={`w-5 h-5 transition-transform ${mobileCrechesOpen ? "rotate-180" : ""}`}
                strokeWidth={2}
              />
            </button>

            <AnimatePresence initial={false}>
              {mobileCrechesOpen && (
                <motion.div
                  className="bg-sage rounded-2xl mt-1 mb-2 overflow-hidden"
                  initial={{ opacity: 0, height: 0 }}
                  animate={{ opacity: 1, height: "auto" }}
                  exit={{ opacity: 0, height: 0 }}
                  transition={{ duration: 0.25, ease: easeSoft }}
                >
                  <ul className="py-2 px-1">
                    {crechesList.map((c) => (
                      <li key={c.slug}>
                        <Link
                          to={`/micro-creches/${c.slug}`}
                          onClick={closeAll}
                          className="block px-4 py-2.5 rounded-xl text-sm text-white/95 hover:bg-white/15"
                        >
                          {c.nom}
                        </Link>
                      </li>
                    ))}
                    <li className="mt-1">
                      <Link
                        to="/nos-micro-creches"
                        onClick={closeAll}
                        className="block px-4 py-2.5 rounded-xl text-sm font-medium text-white/95 hover:bg-white/10 border-t border-white/15"
                      >
                        Voir toutes les crèches →
                      </Link>
                    </li>
                  </ul>
                </motion.div>
              )}
            </AnimatePresence>

            {navLinks.map((l) => (
              <NavLink
                key={l.to}
                to={l.to}
                onClick={closeAll}
                className={({ isActive }) =>
                  `px-3 py-3 rounded-xl text-base font-medium text-ink ${
                    isActive ? "bg-terra-soft text-terra-hover" : "hover:bg-muted"
                  }`
                }
              >
                {l.label}
              </NavLink>
            ))}

            <a
              href={PREINSCRIPTION_URL}
              target="_blank"
              rel="noopener noreferrer"
              onClick={closeAll}
              className="mt-4 flex items-center justify-center gap-2 bg-terra hover:bg-terra-hover text-white font-medium px-5 py-3.5 rounded-2xl shadow-soft-md active:scale-[0.98] transition"
            >
              Pré-inscription
              <ArrowRight size={18} strokeWidth={2} />
            </a>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </header>
  );
}
